import { differenceInCalendarDays } from 'date-fns';

import {
	CreditLineState,
	CreditLineStateNotPaidDueLessThan7DaysTypeEnum,
	CreditLineStateNotPaidDueMissedTypeEnum,
	CreditLineStateNotPaidDueMoreThan7DaysTypeEnum,
	CreditLineStateNotPaidDueTodayTypeEnum,
	CreditLineStateNotPaidDueTomorrowTypeEnum,
	CreditLineStateWaitingStatementTypeEnum,
} from '../../../../types/generated/pos';
import { PermittedNotificationTypes } from '../Notification/config';
import { parseDate } from '@utils/date';
import { formatMoney } from '@utils/formatMoney/formatMoney';
import { getDateWithYear } from '@utils/dates/formatDates';

const formatDate = (serverDate: string): string => {
	const parsedDate = parseDate(serverDate);
	return getDateWithYear(parsedDate);
};

type CreditNotificationInfo = {
	type?: PermittedNotificationTypes;
	notification?: string;
	caption: string;
};

export const getNotificationInfo = (
	creditState: CreditLineState
): CreditNotificationInfo | undefined => {
	if (
		creditState.type ===
		CreditLineStateWaitingStatementTypeEnum.WaitingStatement
	) {
		const { minPaymentCalculationDate, dueDate } = creditState;
		return {
			caption: `Minimum payment will be calculated on ${formatDate(
				minPaymentCalculationDate
			)}.`,
			notification: `You’ll have to pay this by ${formatDate(dueDate)}`,
			type: PermittedNotificationTypes.SUCCESS_NOTIFICATION,
		};
	}

	if (
		creditState.type ===
		CreditLineStateNotPaidDueMoreThan7DaysTypeEnum.NotPaidDueMoreThan7Days
	) {
		const { dueDate, minPayment } = creditState;
		return {
			caption: `Minimum payment of ${formatMoney(
				minPayment
			)} is due on ${formatDate(dueDate)}`,
		};
	}

	if (
		creditState.type ===
		CreditLineStateNotPaidDueLessThan7DaysTypeEnum.NotPaidDueLessThan7Days
	) {
		const { dueDate, minPayment } = creditState;
		const parsedDate = parseDate(dueDate);
		const daysBeforeDueDate = differenceInCalendarDays(parsedDate, new Date());
		const amount = formatMoney(minPayment);
		return {
			caption: `Minimum payment of ${amount} is due on ${formatDate(dueDate)}.`,
			notification: `${daysBeforeDueDate} days before your due date`,
			type: PermittedNotificationTypes.WARNING_NOTIFICATION,
		};
	}

	if (
		creditState.type ===
		CreditLineStateNotPaidDueTomorrowTypeEnum.NotPaidDueTomorrow
	) {
		const { dueDate, minPayment } = creditState;
		const amount = formatMoney(minPayment);
		return {
			caption: `Minimum payment of ${amount} is due on ${formatDate(dueDate)}.`,
			notification: 'Your due date is tomorrow',
			type: PermittedNotificationTypes.WARNING_NOTIFICATION,
		};
	}

	if (
		creditState.type === CreditLineStateNotPaidDueTodayTypeEnum.NotPaidDueToday
	) {
		const { minPayment } = creditState;
		const amount = formatMoney(minPayment);
		return {
			caption: `Minimum payment of ${amount} is due today.`,
			notification: 'Your due date is today',
			type: PermittedNotificationTypes.WARNING_NOTIFICATION,
		};
	}

	if (
		creditState.type ===
		CreditLineStateNotPaidDueMissedTypeEnum.NotPaidDueMissed
	) {
		const { minPayment } = creditState;
		const amount = formatMoney(minPayment);
		return {
			caption: `You’ve missed a payment. Pay ${amount} now.`,
			notification:
				'The credit line is temporarily locked. Please settle the overdue payment to continue using it',
			type: PermittedNotificationTypes.BAD_NOTIFICATION,
		};
	}
};
